import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from 'reactstrap';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTenant, useSuspendTenant, useReactivateTenant } from '../../api/platformTenants';
import { StatusBadge } from '../../components/StatusBadge';
import { TenantSuspendModal } from '../../components/tenants/TenantSuspendModal';
import TenantDetailOverview from './TenantDetailOverview';
import TenantDetailWarehouses from './TenantDetailWarehouses';
import TenantDetailAdmins from './TenantDetailAdmins';
import TenantDetailAudit from './TenantDetailAudit';

type Tab = 'overview' | 'warehouses' | 'admins' | 'audit';

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const tenantId = Number(id);
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') ?? 'overview') as Tab;

  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const { data: tenant, isLoading } = useTenant(tenantId, token);
  const suspendMut = useSuspendTenant(tenantId, token);
  const reactivateMut = useReactivateTenant(tenantId, token);
  const [showSuspend, setShowSuspend] = useState(false);

  if (isLoading || !tenant) return <p>Caricamento…</p>;

  const goto = (next: Tab) => setParams({ tab: next });
  const isSuspended = tenant.status === 'suspended';

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ color: 'var(--vp-text-3)', fontSize: 12 }}>Tenants /</span>
        <span style={{ fontFamily: 'var(--vp-font-mono)', fontWeight: 600 }}>{tenant.code}</span>
        <span>·</span>
        <span>{tenant.name}</span>
        <StatusBadge tone={isSuspended ? 'warn' : 'success'}>{isSuspended ? 'sospeso' : 'attivo'}</StatusBadge>
        <div style={{ marginLeft: 'auto' }}>
          {isSuspended ? (
            <Button type="button" color="primary" size="sm"
                    onClick={() => { if (confirm('Riattivare il tenant?')) reactivateMut.mutate(); }}>
              Riattiva tenant
            </Button>
          ) : (
            <Button type="button" color="danger" size="sm" onClick={() => setShowSuspend(true)}>
              Sospendi tenant
            </Button>
          )}
        </div>
      </header>

      <nav role="tablist" style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--vp-border)' }}>
        {([
          ['overview', 'Panoramica'],
          ['warehouses', 'Magazzini'],
          ['admins', 'Amministratori'],
          ['audit', 'Verifica'],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => goto(k)}
            className="vp-tab"
            style={{
              padding: '10px 16px', border: 'none', background: 'none',
              borderBottom: '2px solid', borderColor: tab === k ? 'var(--vp-primary)' : 'transparent',
              fontWeight: tab === k ? 600 : 400, cursor: 'pointer',
            }}
          >{label}</button>
        ))}
      </nav>

      <div style={{ marginTop: 16 }}>
        {tab === 'overview'   && <TenantDetailOverview tenant={tenant} disabled={isSuspended} />}
        {tab === 'warehouses' && <TenantDetailWarehouses tenantId={tenantId} disabled={isSuspended} />}
        {tab === 'admins'     && <TenantDetailAdmins tenantId={tenantId} disabled={isSuspended} />}
        {tab === 'audit'      && <TenantDetailAudit tenantId={tenantId} />}
      </div>

      <TenantSuspendModal
        isOpen={showSuspend}
        tenantCode={tenant.code}
        onClose={() => setShowSuspend(false)}
        onConfirm={() => { suspendMut.mutate(); setShowSuspend(false); }}
      />
    </div>
  );
}
